// Lesson 3: Aktivitas Sore dan Malam Hari - Question Configurations
export interface QuestionConfig {
    type: string;
    wordBank: number[]; // Word IDs to use
    questionSentence?: number[]; // Order of word IDs for sentence questions
    correctAnswer?: number[] | number; // Correct answer(s)
    options?: Array<{ id: number; text?: string }>; // Multiple choice options
    imagePrompt?: string; // For image-based questions
}

export interface MatchingPair {
    indonesian: string;
    batak: string;
}

// 1. Vocabulary Introduction Config (Image + Word) - 5 questions
export const unit5Lesson3VocabIntroConfigs = [
    {
        type: "vocab-intro",
        introWordId: 301, // "martangiang" (Pulang)
        imagePrompt: "person_going_home"
    },
    {
        type: "vocab-intro",
        introWordId: 302, // "manortor" (Istirahat)
        imagePrompt: "person_resting"
    },
    {
        type: "vocab-intro",
        introWordId: 303, // "mandok TV" (Menonton)
        imagePrompt: "watching_television"
    },
    {
        type: "vocab-intro",
        introWordId: 304, // "mamangan gabeng" (Makan malam)
        imagePrompt: "dinner_time"
    },
    {
        type: "vocab-intro",
        introWordId: 305, // "mamate" (Tidur)
        imagePrompt: "person_sleeping"
    }
];

// 2. Vocabulary Quiz (Image → Choose correct word) - 5 questions
export const unit5Lesson3WordTranslationConfigs: QuestionConfig[] = [
    {
        type: "word-translation",
        wordBank: [301],
        imagePrompt: "person_going_home",
        correctAnswer: 301, // "martangiang"
        options: [
            { id: 301 }, // martangiang
            { id: 302 }, // manortor
            { id: 303 }, // mandok TV
            { id: 305 }  // mamate
        ]
    },
    {
        type: "word-translation",
        wordBank: [305],
        imagePrompt: "person_sleeping",
        correctAnswer: 305, // "mamate"
        options: [
            { id: 305 }, // mamate
            { id: 301 }, // martangiang
            { id: 302 }, // manortor
            { id: 304 }  // mamangan gabeng
        ]
    },
    {
        type: "word-translation",
        wordBank: [303],
        imagePrompt: "watching_television",
        correctAnswer: 303, // "mandok TV"
        options: [
            { id: 303 }, // mandok TV
            { id: 302 }, // manortor
            { id: 304 }, // mamangan gabeng
            { id: 301 }  // martangiang
        ]
    },
    {
        type: "word-translation",
        wordBank: [304],
        imagePrompt: "dinner_time",
        correctAnswer: 304, // "mamangan gabeng"
        options: [
            { id: 304 }, // mamangan gabeng
            { id: 305 }, // mamate
            { id: 303 }, // mandok TV
            { id: 302 }  // manortor
        ]
    },
    {
        type: "word-translation",
        wordBank: [302],
        imagePrompt: "person_resting",
        correctAnswer: 302, // "manortor"
        options: [
            { id: 302 }, // manortor
            { id: 305 }, // mamate
            { id: 301 }, // martangiang
            { id: 304 }  // mamangan gabeng
        ]
    }
];

// 3. Fill in the Blank Questions - 5 questions
export const unit5Lesson3FillInBlankConfigs: QuestionConfig[] = [
    {
        type: "fill-in-the-blank",
        wordBank: [107, 999, 303, 206, 209],
        questionSentence: [107, 999, 303, 206, 209], // "Ia _____ TV di bagas."
        correctAnswer: 303, // "mandok"
        options: [
            { id: 303 }, // mandok
            { id: 301 }, // martangiang
            { id: 302 }, // manortor
            { id: 305 }  // mamate
        ]
    },
    {
        type: "fill-in-the-blank",
        wordBank: [306, 999, 307, 308],
        questionSentence: [306, 999, 307, 308], // "Hami _____ dari huria."
        correctAnswer: 301, // "martangiang"
        options: [
            { id: 301 }, // martangiang
            { id: 302 }, // manortor
            { id: 304 }, // mamangan gabeng
            { id: 305 }  // mamate
        ]
    },
    {
        type: "fill-in-the-blank",
        wordBank: [310, 999, 309],
        questionSentence: [310, 999, 309], // "Ise _____ gabeng."
        correctAnswer: 305, // "mamate"
        options: [
            { id: 305 }, // mamate
            { id: 301 }, // martangiang
            { id: 303 }, // mandok
            { id: 302 }  // manortor
        ]
    },
    {
        type: "fill-in-the-blank",
        wordBank: [105, 999, 206, 209],
        questionSentence: [105, 999, 206, 209], // "Au _____ di bagas."
        correctAnswer: 302, // "manortor"
        options: [
            { id: 302 }, // manortor
            { id: 304 }, // mamangan gabeng
            { id: 305 }, // mamate
            { id: 303 }  // mandok
        ]
    },
    {
        type: "fill-in-the-blank",
        wordBank: [306, 999, 206, 209],
        questionSentence: [306, 999, 206, 209], // "Hami _____ di bagas."
        correctAnswer: 304, // "mamangan gabeng"
        options: [
            { id: 304 }, // mamangan gabeng
            { id: 301 }, // martangiang
            { id: 302 }, // manortor
            { id: 303 }  // mandok
        ]
    }
];

// 4. Compose Sentence Questions - 5 questions
export const unit5Lesson3ComposeSentenceConfigs: QuestionConfig[] = [
    {
        type: "compose-sentences",
        wordBank: [306, 301, 307, 308],
        questionSentence: [306, 301, 307, 308], // "Hami martangiang dari huria."
        correctAnswer: [306, 301, 307, 308],
        imagePrompt: "people_coming_home_from_church"
    },
    {
        type: "compose-sentences",
        wordBank: [107, 303, 206, 209],
        questionSentence: [107, 303, 206, 209], // "Ia mandok TV di bagas."
        correctAnswer: [107, 303, 206, 209],
        imagePrompt: "person_watching_tv_home"
    },
    {
        type: "compose-sentences",
        wordBank: [310, 305, 309],
        questionSentence: [310, 305, 309], // "Ise mamate gabeng."
        correctAnswer: [310, 305, 309],
        imagePrompt: "people_sleeping_night"
    },
    {
        type: "compose-sentences",
        wordBank: [105, 302, 206, 209],
        questionSentence: [105, 302, 206, 209], // "Au manortor di bagas."
        correctAnswer: [105, 302, 206, 209],
        imagePrompt: "person_resting_home"
    },
    {
        type: "compose-sentences",
        wordBank: [306, 304, 206, 209],
        questionSentence: [306, 304, 206, 209], // "Hami mamangan gabeng di bagas."
        correctAnswer: [306, 304, 206, 209],
        imagePrompt: "family_dinner_home"
    }
];

// 5. Matching Questions - 5 questions
export const unit5Lesson3MatchingConfigs: QuestionConfig[] = [
    {
        type: "matching",
        wordBank: [301, 302, 303],
        correctAnswer: [301, 302, 303],
        options: [
            { id: 301, text: "Pulang" },
            { id: 302, text: "Istirahat" },
            { id: 303, text: "Menonton" }
        ]
    },
    {
        type: "matching",
        wordBank: [304, 305, 306],
        correctAnswer: [304, 305, 306],
        options: [
            { id: 304, text: "Makan malam" },
            { id: 305, text: "Tidur" },
            { id: 306, text: "Kami" }
        ]
    },
    {
        type: "matching",
        wordBank: [307, 308, 309],
        correctAnswer: [307, 308, 309],
        options: [
            { id: 307, text: "dari" },
            { id: 308, text: "Gereja" },
            { id: 309, text: "Malam" }
        ]
    },
    {
        type: "matching",
        wordBank: [105, 106, 107],
        correctAnswer: [105, 106, 107],
        options: [
            { id: 105, text: "Saya" },
            { id: 106, text: "Kamu" },
            { id: 107, text: "Dia" }
        ]
    },
    {
        type: "matching",
        wordBank: [301, 305, 310],
        correctAnswer: [301, 305, 310],
        options: [
            { id: 301, text: "Pulang" },
            { id: 305, text: "Tidur" },
            { id: 310, text: "Mereka" }
        ]
    }
];

export const unit5Lesson3WordMatchingPairs: MatchingPair[] = [
    { indonesian: "Pulang", batak: "ᯔᯀᯒᯖᯀᯰᯎᯤᯀᯰ" },
    { indonesian: "Istirahat", batak: "ᯔᯀᯉᯬᯒᯖᯬᯒ" },
    { indonesian: "Menonton", batak: "ᯔᯀᯉ᯲ᯑᯬᯂ᯲ ᯖᯜ" },
    { indonesian: "Makan malam", batak: "ᯔᯀᯔᯀᯰᯀᯉ ᯎᯀᯄᯰ" },
    { indonesian: "Tidur", batak: "ᯔᯀᯔᯀᯖᯧ" },
    { indonesian: "Kami", batak: "ᯂᯀᯔᯤ" },
    { indonesian: "Gereja", batak: "ᯂᯮᯒᯤᯀ" },
    { indonesian: "Malam", batak: "ᯎᯀᯄᯰ" },
    { indonesian: "Mereka", batak: "ᯀᯤᯘᯧ" }
];

export const unit5Lesson3MatchingWordsConfig = {
    type: "matching-words",
    pairs: unit5Lesson3WordMatchingPairs,
};
